import { TAX_ERROR, CLEAR_TAX_ERROR, FETCH_SHIPPING_QUOTE_ERROR } from '../../../../actions/types'
import branditRails from '../../../../api/brandit_rails'
import { createTaxFormData, shouldCalculateTax } from '../../../../components/Checkout/services/taxCalculationService'
import {
  clearCheckoutTaxErrorsAction,
  setCheckoutTaxErrorsAction,
  setCheckoutTaxQuoteAction,
  setCheckoutTaxStatusAction
} from './checkoutTax.actions'

const INVALID_POSTAL_STATE_ERROR_TEXT =
  'The provided address contains a postal code and state combination that is not valid.'

let abortController

const isAbortError = error => error?.name === 'AbortError' || error?.code === 'ERR_CANCELED'

const getTaxInputsFromState = state => {
  return {
    checkout: state.checkout,
    checkoutRates: state.checkoutRates,
    cartItems: state.cartItems,
    currentUser: state.currentUser,
    portal: state.portal,
    managedCustomerAddresses: state.managed_customer_addresses
  }
}

const canCalculateTaxFromState = ({ checkout, cartItems, portal, currentUser }) => {
  const isCartLoaded = Array.isArray(cartItems) && cartItems.length > 0

  return shouldCalculateTax({
    isInitializing: false,
    isCartLoaded,
    cartItems,
    checkout,
    portal,
    currentUser
  })
}

const buildTaxFormDataFromState = inputs => {
  return createTaxFormData({
    checkout: inputs.checkout,
    checkoutRates: inputs.checkoutRates,
    cartItems: inputs.cartItems,
    currentUser: inputs.currentUser,
    portal: inputs.portal,
    managedCustomerAddresses: inputs.managedCustomerAddresses
  })
}

const dispatchLegacyTaxErrors = (dispatch, responseData) => {
  if (responseData?.error_text === INVALID_POSTAL_STATE_ERROR_TEXT) {
    dispatch({
      type: TAX_ERROR,
      payload: {
        message:
          "ERROR CALCULATING TAX RATE: We couldn't calculate the tax rate based on the address provided, please review your address to ensure it is entered correctly."
      }
    })

    dispatch({
      type: FETCH_SHIPPING_QUOTE_ERROR,
      payload:
        "ERROR CALCULATING SHIPPING RATE: We couldn't calculate the shipping rate based on the address provided, please review your address to ensure it is entered correctly."
    })

    return
  }

  dispatch({
    type: TAX_ERROR,
    payload: {
      message:
        'There has been a problem estimating sales tax on your order, please check your delivery address and try again'
    }
  })
}

export const fetchTaxQuote = formData => async dispatch => {
  if (abortController) {
    abortController.abort()
  }
  abortController = new AbortController()

  dispatch(setCheckoutTaxStatusAction('loading'))
  dispatch(clearCheckoutTaxErrorsAction())

  try {
    const response = await branditRails.post('/tax', formData, {
      signal: abortController.signal
    })

    if (!response) {
      dispatch(setCheckoutTaxStatusAction('failed'))
      dispatch(setCheckoutTaxErrorsAction({ action: 'fetchTaxQuote', message: 'No response from tax endpoint' }))
      return { ok: false, response: null }
    }

    if (response.data?.tax_status === 'FAILED') {
      dispatch(setCheckoutTaxStatusAction('failed'))
      dispatch(
        setCheckoutTaxErrorsAction({
          action: 'fetchTaxQuote',
          message: response.data?.error_text || 'Tax calculation failed'
        })
      )

      // Keep legacy error channels for existing UI
      dispatchLegacyTaxErrors(dispatch, response.data)
      return { ok: false, response }
    }

    dispatch({ type: CLEAR_TAX_ERROR, payload: response.data })
    dispatch(setCheckoutTaxQuoteAction(response.data))
    dispatch(setCheckoutTaxStatusAction('succeeded'))

    return { ok: true, response }
  } catch (error) {
    if (isAbortError(error)) {
      // Abort is expected when inputs change quickly.
      // Don't flip status to failed; caller can decide next step.
      return { ok: false, aborted: true }
    }

    dispatch(setCheckoutTaxStatusAction('failed'))
    dispatch(
      setCheckoutTaxErrorsAction({
        action: 'fetchTaxQuote',
        message: error?.message || 'There has been a problem with fetching tax information'
      })
    )

    dispatch({
      type: TAX_ERROR,
      payload: { message: 'There has been a problem with fetching tax information' }
    })

    return { ok: false, error }
  }
}

export const recalculateTaxIfReady = () => async (dispatch, getState) => {
  const state = getState()
  const inputs = getTaxInputsFromState(state)

  if (!canCalculateTaxFromState(inputs)) return false

  const formData = buildTaxFormDataFromState(inputs)
  const result = await dispatch(fetchTaxQuote(formData))

  return result?.ok === true
}
